import React, { useState } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px 40px;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 600;
  color: #333;
`;

const Subtitle = styled.p`
  margin: 0 0 32px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const Required = styled.span`
  color: #dc3545;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
`;

const EyeIcon = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #666;
  font-size: 20px;
  
  &:hover {
    color: #333;
  }
`;

const ForgotPassword = styled.a`
  display: block;
  text-align: right;
  font-size: 14px;
  color: #007bff;
  text-decoration: none;
  margin-bottom: 24px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  background: #007bff;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 24px;
  
  &:hover {
    background: #0056b3;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,123,255,0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const SignupText = styled.p`
  text-align: center;
  font-size: 14px;
  color: #666;
  margin: 0;
  
  a {
    color: #007bff;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  color: #333;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  
  &:hover {
    background: #f8f9fa;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const GoogleIcon = styled.div`
  width: 20px;
  height: 20px;
  background-color: #4285f4;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 12px;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e0e0e0;
  }
  
  span {
    padding: 0 16px;
    color: #999;
    font-size: 14px;
  }
`;

interface LoginModalProps {
  onLogin: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleGoogleLogin = () => {
    console.log('Logging in with Google...');
    onLogin();
  };

  const handleEmailLogin = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Logging in with email:', email);
    // For now, just redirect to Auth0
    onLogin();
  };

  return (
    <ModalOverlay>
      <LoginCard>
        <Title>Welcome</Title>
        <Subtitle>Log in to AGEST-ID to continue to Test Result Check.</Subtitle>
        
        <form onSubmit={handleEmailLogin}>
          <FormField>
            <Label>
              Email address <Required>*</Required>
            </Label>
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FormField>

          <FormField>
            <Label>
              Password <Required>*</Required>
            </Label>
            <PasswordWrapper>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <EyeIcon type="button" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </EyeIcon>
            </PasswordWrapper>
          </FormField>

          <ForgotPassword href="#" onClick={(e) => { e.preventDefault(); }}>
            Forgot password?
          </ForgotPassword>

          <PrimaryButton type="submit">
            Log in
          </PrimaryButton>
        </form>

        <Divider>
          <span>OR</span>
        </Divider>
        
        <LoginButton type="button" onClick={handleGoogleLogin}>
          <GoogleIcon>G</GoogleIcon>
          Log in with Google
        </LoginButton>

        <SignupText>
          Don't have an account? <a href="#" onClick={(e) => { e.preventDefault(); onLogin(); }}>Sign up</a>
        </SignupText>
      </LoginCard>
    </ModalOverlay>
  );
};

export default LoginModal;

